// FluidTracker.js — OLM1 fluid door history for any time period.
// Volume: FLUID_TRACKER_<date>/<shift>.hours["HH:00"].rows (backend/fluid_tracker.py,
//   saved each shift hour): per door On trailer / Weight / Avg wt / Packages loaded.
// Blocking: JamBot AGL ("Full Alarm") events from JAMCTRL_AUTO_<date>/<shift>, mapped to
//   the sorter chute that feeds each Fluid Direct door (DD103–DD116 = chutes 1–14).
//   Offline doors (DD117–DD134) are not fed by the sorter chutes, so no AGL for them.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BEDS, DEFAULT_NAMES } from '../sorterLayout';
import { locate, heat } from './SorterMap';

const pad = n => String(n).padStart(2, '0');
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmt = v => (v == null ? '—' : Math.round(v).toLocaleString());
const DIRECT = Array.from({ length: 14 }, (_, i) => `DD${103 + i}`);
const OFFLINE = Array.from({ length: 18 }, (_, i) => `DD${117 + i}`);
const SPANS = { shift: 'Selected shift', day: 'Selected day (both shifts)', week: 'Last 7 days', custom: 'Custom dates' };
const METRICS = { loaded: 'Packages loaded', agl: 'AGL events (lane full)', aglmin: 'AGL minutes' };

function daysBetween(from, to, cap = 31) {
  const out = [], d = new Date(`${from}T12:00:00`), end = new Date(`${to}T12:00:00`);
  while (d <= end && out.length < cap) { out.push(ymd(d)); d.setDate(d.getDate() + 1); }
  return out;
}
function defaultShift() {
  const now = new Date(), m = now.getHours() * 60 + now.getMinutes(), d = new Date(now);
  if (m < 8 * 60) { d.setDate(d.getDate() - 1); return { date: ymd(d), shift: 'night' }; }
  return { date: ymd(d), shift: m >= 18 * 60 + 45 ? 'night' : 'day' };
}
// Absolute start (ms) of an hour label inside a shift record (night hours after midnight are the next day).
export function hourStart(date, shift, label) {
  const h = Number(String(label).slice(0, 2));
  const d = new Date(`${date}T${pad(h)}:00:00`);
  if (shift === 'night' && h < 12) d.setDate(d.getDate() + 1);
  return d.getTime();
}
// AGL source -> Fluid Direct door ("DD109") via the chute bed names, else null.
export function doorOfAgl(src) {
  const loc = locate(src);
  const name = loc.bed && DEFAULT_NAMES[loc.bed];
  return name ? name.split('-')[0] : null;
}

// Combine hourly records + AGL events (pure; unit-tested).
export function buildFluidStats(hourRecs, aglEvents) {
  const doors = {}, hours = new Set();
  const D = d => (doors[d] = doors[d] || { door: d, loaded: 0, hoursSeen: 0, peak: 0, peakAt: null, arcs: new Set(), byHour: {}, agl: 0, aglMin: 0, aglByHour: {}, last: null });
  for (const h of hourRecs) {
    hours.add(h.t);
    for (const r of h.rows || []) {
      const x = D(r.door);
      x.loaded += r.loaded || 0; x.hoursSeen += 1; x.byHour[h.t] = (x.byHour[h.t] || 0) + (r.loaded || 0);
      if ((r.loaded || 0) > x.peak) { x.peak = r.loaded; x.peakAt = h.t; }
      if (r.arc) x.arcs.add(r.arc);
      if (!x.last || h.t >= x.last.t) x.last = { t: h.t, count: r.count, weight: r.weight, avgWt: r.avgWt, arc: r.arc };
    }
  }
  for (const e of aglEvents) {
    const d = doorOfAgl(e.src);
    if (!d) continue;
    const x = D(d), s = Number(e.startTs) * 1000, mins = Math.max(1, Math.round((Number(e.endTs) - Number(e.startTs)) / 60));
    const hb = Math.floor(s / 3600000) * 3600000;
    x.agl += 1; x.aglMin += mins; x.aglByHour[hb] = (x.aglByHour[hb] || 0) + 1;
    hours.add(hb);
  }
  const list = Object.values(doors).map(x => ({ ...x, arcs: [...x.arcs], perHour: x.hoursSeen ? x.loaded / x.hoursSeen : 0 }));
  return { doors: Object.fromEntries(list.map(x => [x.door, x])), hours: [...hours].sort((a, b) => a - b) };
}

export default function FluidTracker({ api, site }) {
  const init = useMemo(defaultShift, []);
  const [date, setDate] = useState(init.date);
  const [shift, setShift] = useState(init.shift);
  const [span, setSpan] = useState('shift');
  const [from, setFrom] = useState(init.date);
  const [to, setTo] = useState(init.date);
  const [metric, setMetric] = useState('loaded');
  const [recs, setRecs] = useState([]);
  const [agl, setAgl] = useState([]);
  const [status, setStatus] = useState('');

  const get = useCallback((d, st) => fetch(`${api}/api/fclm/cache?site=${site}&date=${d}&shift_type=${st}`).then(r => (r.ok ? r.json() : null)).catch(() => null), [api, site]);
  const keys = useMemo(() => {
    if (span === 'shift') return [[date, shift]];
    const days = span === 'day' ? [date] : span === 'week'
      ? daysBetween(ymd(new Date(new Date(`${date}T12:00:00`).getTime() - 6 * 864e5)), date)
      : daysBetween(from <= to ? from : to, from <= to ? to : from);
    return days.flatMap(d => [[d, 'day'], [d, 'night']]);
  }, [span, date, shift, from, to]);
  const load = useCallback(async () => {
    setStatus('Loading…');
    const [ft, ja] = await Promise.all([Promise.all(keys.map(([d, s]) => get(`FLUID_TRACKER_${d}`, s))), Promise.all(keys.map(([d, s]) => get(`JAMCTRL_AUTO_${d}`, s)))]);
    const hr = [];
    ft.forEach((x, i) => {
      const [d, s] = keys[i];
      for (const [label, h] of Object.entries((x && x.hours) || {})) hr.push({ t: hourStart(d, s, label), date: d, shift: s, label, rows: h.rows || [] });
    });
    setRecs(hr);
    setAgl(ja.flatMap(x => (x && Array.isArray(x.events) ? x.events : [])).filter(e => e && e.type === 'AGL'));
    setStatus(`${hr.length} hourly snapshot(s) · ${keys.length} shift(s)`);
  }, [get, keys]);
  useEffect(() => { load(); const t = setInterval(load, 5 * 60 * 1000); return () => clearInterval(t); }, [load]);

  const st = useMemo(() => buildFluidStats(recs, agl), [recs, agl]);
  const val = (x, m) => (!x ? 0 : m === 'loaded' ? x.loaded : m === 'agl' ? x.agl : x.aglMin);
  // Hour columns for the grid: individual hours up to 48, else one column per day.
  const byDay = st.hours.length > 48;
  const cols = byDay ? [...new Set(st.hours.map(t => ymd(new Date(t))))] : st.hours;
  const colLabel = c => (byDay ? c.slice(5) : new Date(c).toLocaleString([], { month: 'numeric', day: 'numeric', hour: '2-digit' }));
  const cellOf = (x, c) => {
    if (!x) return { v: 0, a: 0 };
    if (!byDay) return { v: x.byHour[c] || 0, a: x.aglByHour[c] || 0 };
    const sum = o => Object.entries(o).filter(([t]) => ymd(new Date(Number(t))) === c).reduce((s, [, v]) => s + v, 0);
    return { v: sum(x.byHour), a: sum(x.aglByHour) };
  };
  const gridMax = Math.max(1, ...Object.values(st.doors).flatMap(x => cols.map(c => cellOf(x, c).v)));

  // Chute view (cropped Sorter Map): chute beds heat = AGL events for the range; volume under each chute.
  const chuteBeds = BEDS.filter(b => DEFAULT_NAMES[b[0]]);
  const bedAgl = useMemo(() => {
    const m = {};
    for (const e of agl) { const b = locate(e.src).bed; if (b && DEFAULT_NAMES[b]) m[b] = (m[b] || 0) + (metric === 'aglmin' ? Math.max(1, Math.round((e.endTs - e.startTs) / 60)) : 1); }
    return m;
  }, [agl, metric]);
  const bedMax = Math.max(1, ...Object.values(bedAgl));
  const V = { x: 470, y: 960, w: 920, h: 520 };

  const card = { background: '#fff', border: '1px solid #E7E9E9', borderRadius: 8, padding: 16, marginBottom: 16 };
  const th = { textAlign: 'right', fontSize: 11, color: '#697576', padding: '5px 6px', borderBottom: '1px solid #E7E9E9', whiteSpace: 'nowrap' };
  const td = { fontSize: 12, padding: '4px 6px', borderBottom: '1px solid #E7E9E9', textAlign: 'right', whiteSpace: 'nowrap' };
  if (site !== 'OLM1') return <div><h2 className="section-title">Fluid Tracker — {site}</h2><p>OLM1 only.</p></div>;

  const doorRow = d => {
    const x = st.doors[d];
    return (
      <tr key={d}>
        <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{d}</td>
        <td style={{ ...td, textAlign: 'left' }}>{x ? x.arcs.join(', ') || '—' : '—'}</td>
        <td style={{ ...td, fontWeight: 700 }}>{fmt(x?.loaded)}</td><td style={td}>{x?.hoursSeen ? x.perHour.toFixed(0) : '—'}</td>
        <td style={td}>{x?.peak ? `${fmt(x.peak)} @ ${new Date(x.peakAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '—'}</td>
        <td style={td}>{x?.last ? fmt(x.last.count) : '—'}</td><td style={td}>{x?.last ? x.last.avgWt?.toFixed?.(1) ?? '—' : '—'}</td>
        <td style={{ ...td, color: x?.agl ? '#DE1B1B' : '#BBC0C1' }}>{DIRECT.includes(d) ? fmt(x?.agl || 0) : 'n/a'}</td>
        <td style={td}>{DIRECT.includes(d) ? fmt(x?.aglMin || 0) : 'n/a'}</td>
        <td style={td}>{DIRECT.includes(d) && x?.agl ? fmt(x.loaded / x.agl) : '—'}</td>
      </tr>
    );
  };
  const tot = ds => ds.reduce((s, d) => s + (st.doors[d]?.loaded || 0), 0);

  return (
    <div>
      <h2 className="section-title">Fluid Tracker — {site}</h2>
      <div style={{ ...card, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12 }}>Timespan<br /><select value={span} onChange={e => setSpan(e.target.value)} aria-label="Timespan">{Object.entries(SPANS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        {span === 'custom'
          ? <><label style={{ fontSize: 12 }}>From<br /><input type="date" value={from} onChange={e => setFrom(e.target.value)} aria-label="From date" /></label>
            <label style={{ fontSize: 12 }}>To<br /><input type="date" value={to} onChange={e => setTo(e.target.value)} aria-label="To date" /></label></>
          : <label style={{ fontSize: 12 }}>Date<br /><input type="date" value={date} onChange={e => setDate(e.target.value)} aria-label="Date" /></label>}
        {span === 'shift' && <label style={{ fontSize: 12 }}>Shift<br /><select value={shift} onChange={e => setShift(e.target.value)} aria-label="Shift"><option value="day">Day</option><option value="night">Night</option></select></label>}
        <label style={{ fontSize: 12 }}>Grid shows<br /><select value={metric} onChange={e => setMetric(e.target.value)} aria-label="Metric">{Object.entries(METRICS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <span style={{ fontSize: 12, color: '#697576' }}>{status} · Fluid Direct {fmt(tot(DIRECT))} · Fluid Offline {fmt(tot(OFFLINE))} packages loaded</span>
      </div>

      <div style={card}>
        <strong style={{ fontSize: 14 }}>By door</strong>
        <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
          <thead><tr>{['Door', 'ARC(s)', 'Pkgs loaded', 'Avg / hr', 'Peak hour', 'On trailer (latest)', 'Avg wt (latest)', 'AGL events', 'AGL min', 'Pkgs per AGL'].map((h, i) => <th key={h} style={{ ...th, textAlign: i < 2 ? 'left' : 'right' }}>{h}</th>)}</tr></thead>
          <tbody>
            <tr><td colSpan={10} style={{ ...td, textAlign: 'left', fontWeight: 700, background: '#F5F6F6' }}>Fluid Direct (DD103–116)</td></tr>
            {DIRECT.map(doorRow)}
            <tr><td colSpan={10} style={{ ...td, textAlign: 'left', fontWeight: 700, background: '#F5F6F6' }}>Fluid Offline (DD117–134)</td></tr>
            {OFFLINE.filter(d => st.doors[d]).map(doorRow)}
          </tbody>
        </table>
      </div>

      <div style={card}>
        <strong style={{ fontSize: 14 }}>{METRICS[metric]} by door and {byDay ? 'day' : 'hour'}</strong>
        {cols.length === 0 ? <p style={{ fontSize: 13, color: '#697576' }}>No data in this timespan yet. Hourly snapshots are saved by the Fluid Tracker poster each shift hour.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', marginTop: 8 }}>
              <thead><tr><th style={{ ...th, textAlign: 'left' }}>Door</th>{cols.map(c => <th key={c} style={th}>{colLabel(c)}</th>)}</tr></thead>
              <tbody>
                {[...DIRECT, ...OFFLINE].filter(d => st.doors[d]).map(d => (
                  <tr key={d}><td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{d}</td>
                    {cols.map(c => {
                      const { v, a } = cellOf(st.doors[d], c);
                      const shown = metric === 'loaded' ? v : a;
                      const m = metric === 'loaded' ? gridMax : Math.max(1, ...Object.values(st.doors).flatMap(x => cols.map(cc => cellOf(x, cc).a)));
                      return <td key={c} style={{ ...td, background: shown ? heat(shown, m) : undefined }} title={`${d} ${colLabel(c)}: ${v} pkgs loaded · ${a} AGL events`}>{shown ? fmt(shown) : ''}{metric === 'loaded' && a ? <span style={{ color: '#DE1B1B', fontSize: 10 }}> ●{a}</span> : null}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ fontSize: 11, color: '#697576', marginTop: 4 }}>{metric === 'loaded' ? 'Red ●n = AGL (lane full) events on that door’s chute in the same period (Fluid Direct only).' : 'AGL events counted by start time.'}</div>
      </div>
      <div style={card}>
        <strong style={{ fontSize: 14 }}>Sorter chutes → Fluid Direct doors — {metric === 'aglmin' ? 'AGL minutes' : 'AGL events'} per bed · packages loaded per door</strong>
        <svg viewBox={`${V.x} ${V.y} ${V.w} ${V.h}`} style={{ width: '100%', maxWidth: 1100 }} role="img" aria-label="Sorter chutes with lane-full events and packages loaded per door">
          <image href={`${process.env.PUBLIC_URL || ''}/sorter-mimic.png`} x={70} y={50} width={2130} height={1350} />
          {chuteBeds.map(([id, x, y, w, h]) => {
            const v = bedAgl[id] || 0;
            return <g key={id}>
              <rect x={x - 4} y={y - 4} width={w + 8} height={h + 8} rx="6" fill={v ? heat(v, bedMax) : 'rgba(255,255,255,0.01)'} fillOpacity={v ? 0.92 : 1} stroke={v ? '#232F3E' : 'rgba(7,115,152,0.35)'} strokeWidth={v ? 2 : 1.5}><title>{`${DEFAULT_NAMES[id]} (${id}): ${v} ${metric === 'aglmin' ? 'AGL minutes' : 'AGL events'}`}</title></rect>
              {v > 0 && <text x={x + w / 2} y={y + h / 2 + 6} fontSize="17" fontWeight="700" textAnchor="middle" fill={v / bedMax > 0.6 ? '#fff' : '#232F3E'}>{v}</text>}
            </g>;
          })}
          {chuteBeds.filter(b => DEFAULT_NAMES[b[0]].endsWith('-1')).map(([id, x, , w]) => {
            const d = DEFAULT_NAMES[id].split('-')[0];
            return <g key={`l${id}`}>
              <text x={x + w / 2} y={1400} fontSize="16" fontWeight="700" textAnchor="middle" fill="#232F3E">{d}</text>
              <text x={x + w / 2} y={1424} fontSize="15" fontWeight="700" textAnchor="middle" fill="#077398">{fmt(st.doors[d]?.loaded || 0)}</text>
              <text x={x + w / 2} y={1444} fontSize="12" textAnchor="middle" fill="#697576">pkgs</text>
            </g>;
          })}
        </svg>
        <div style={{ fontSize: 11, color: '#697576' }}>Badges = AGL (Full Alarm) on each chute bed in the timespan; blue = packages loaded at the door it feeds. Background = SCADA sorter screen (reference snapshot).</div>
      </div>
    </div>
  );
}
